// src/components/admin/NoticiasAdmin.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
    Box, Paper, Stack, Button, IconButton, TextField, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert, Typography, Divider
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { Add, Edit, Delete, Refresh, Search } from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import RichTextEditor from "../Utiles/RichTextEditor";

// ---------- Utils ----------
const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    const txt = tmp.textContent || tmp.innerText || "";
    return txt.replace(/\s+/g, " ").trim();
};
const toYMD = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "string") {
        const ymd = v.split("T")[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
    }
    return null;
};
const formatDDMMYYYY = (v) => {
    if (!v) return "—";
    const ymd = toYMD(v);
    if (!ymd) return "—";
    const [y, m, d] = ymd.split("-");
    return `${d}-${m}-${y}`;
};
const truncate = (s, max = 120) => (!s ? "" : (s.length <= max ? s : s.slice(0, max) + "…"));

const normNoticia = (n = {}) => ({
    id: n.id,
    nombre: n.nombre ?? "",
    descripcion: n.descripcion ?? "",
    fechaFin: toYMD(n.fechaFin ?? n.fecha_fin ?? null),
});
const denormNoticia = (n = {}) => ({
    id: n.id,
    nombre: n.nombre ?? "",
    descripcion: n.descripcion ?? "",
    fecha_fin: n.fechaFin ? String(n.fechaFin).slice(0, 10) : null,
});

const EMPTY_FORM = { nombre: "", descripcion: "", fechaFin: "" };

const NoticiaSchema = Yup.object({
    nombre: Yup.string().max(200, "Máx. 200 caracteres").required("Obligatorio"),
    descripcion: Yup.string().nullable(),
    fechaFin: Yup.date().nullable().typeError("Fecha no válida"),
});

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const normPage = (p = {}) => ({
    content: Array.isArray(p.content) ? p.content : [],
    totalElements:
        (Number.isFinite(p.totalElements) ? p.totalElements : null) ??
        (Number.isFinite(p.total_elements) ? p.total_elements : 0),
    totalPages:
        (Number.isFinite(p.totalPages) ? p.totalPages : null) ??
        (Number.isFinite(p.total_pages) ? p.total_pages : 0),
    number:
        (Number.isFinite(p.number) ? p.number : null) ??
        (Number.isFinite(p.pageable?.page_number) ? p.pageable.page_number : 0),
});

const NoticiasAdmin = () => {
    const API = process.env.REACT_APP_API_URL || "http://localhost:8080";
    const { token } = useAuth();

    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([
        { field: "fechaFin", sort: "desc" },
        { field: "id", sort: "desc" },
    ]);
    const [q, setQ] = useState("");
    const [qDraft, setQDraft] = useState("");

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [okMsg, setOkMsg] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    const headers = useMemo(() => ({
        Authorization: `Bearer ${token}`,
    }), [token]);

    // Convierte el sortModel del DataGrid en array de "campo,direccion"
    const sortsParam = useMemo(() => {
        if (!Array.isArray(sortModel) || sortModel.length === 0) return undefined;
        const cleaned = sortModel
            .filter(s => s?.field && s?.sort)
            .map(s => `${s.field},${s.sort}`);
        return cleaned.length ? cleaned : undefined;
    }, [sortModel]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;

            // ⚠️ Construimos los params como URLSearchParams para que cada sort vaya repetido
            const params = new URLSearchParams();
            params.append("page", String(page));
            params.append("size", String(pageSize));

            const sorts = sortsParam ?? ["fechaFin,desc", "id,desc"];
            sorts.forEach(s => params.append("sort", s));

            if (q?.trim()) params.append("q", q.trim());

            const { data } = await axios.get(`${API}/api/admin/noticias`, {
                params,    // URLSearchParams evita los "sort[]="
                headers,
            });

            const pageObj = normPage(data);
            const content = pageObj.content.map(normNoticia);

            if (content.length === 0 && pageObj.totalElements > 0 && page >= pageObj.totalPages) {
                const last = Math.max(0, pageObj.totalPages - 1);
                setPaginationModel(pm => ({ ...pm, page: last }));
                return;
            }

            setRows(content);
            setRowCount(pageObj.totalElements);
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || "Error cargando noticias");
        } finally {
            setLoading(false);
        }
    }, [API, headers, paginationModel, sortsParam, q]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setPaginationModel(pm => ({ ...pm, page: 0 }));
    }, [q]);

    const handleRefresh = () => fetchData();

    const handleOpenCreate = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const handleOpenEdit = (row) => {
        setEditing(row);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => setDialogOpen(false);

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = denormNoticia(values);
            if (editing) {
                await axios.put(`${API}/api/admin/noticias/${editing.id}`, payload, { headers });
                setOkMsg("Noticia actualizada");
            } else {
                await axios.post(`${API}/api/admin/noticias`, payload, { headers });
                setOkMsg("Noticia creada");
            }
            setDialogOpen(false);
            setPaginationModel((pm) => ({ ...pm, page: 0 }));
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || "Error al guardar la noticia");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAskDelete = (id) => setDeleteId(id);
    const handleCancelDelete = () => setDeleteId(null);

    const handleConfirmDelete = async () => {
        if (!deleteId) return;
        try {
            await axios.delete(`${API}/api/admin/noticias/${deleteId}`, { headers });
            setOkMsg("Noticia eliminada");
            setDeleteId(null);
            if (rows.length === 1 && paginationModel.page > 0) {
                setPaginationModel((pm) => ({ ...pm, page: pm.page - 1 }));
            } else {
                fetchData();
            }
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || "Error al eliminar");
        }
    };

    const columns = useMemo(() => ([
        { field: "id", headerName: "ID", width: 90, sortable: true },
        { field: "nombre", headerName: "Nombre", flex: 1, minWidth: 220, sortable: true },
        {
            field: "descripcion",
            headerName: "Descripción (preview)",
            flex: 2,
            minWidth: 280,
            sortable: false,
            renderCell: (params) => {
                const html = params?.row?.descripcion || "";
                const txt = truncate(stripHtml(html), 140);
                return <span title={stripHtml(html)}>{txt || "—"}</span>;
            }
        },
        {
            field: "fechaFin",
            headerName: "Fecha fin",
            width: 140,
            sortable: true,
            valueGetter: (params) => params?.row?.fechaFin ?? null,
            renderCell: (params) => {
                const ymd = params?.row?.fechaFin;
                return <span>{formatDDMMYYYY(ymd)}</span>;
            },
            sortComparator: (a, b) => {
                if (!a && !b) return 0;
                if (!a) return 1;
                if (!b) return -1;
                return String(a).localeCompare(String(b));
            },
        },
        {
            field: "acciones",
            headerName: "Acciones",
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params?.row;
                if (!row) return null;
                return (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
                        <Tooltip title="Editar">
                            <IconButton color="primary" size="small" sx={{ p: 0.5 }} onClick={() => handleOpenEdit(row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                            <IconButton color="error" size="small" sx={{ p: 0.5 }} onClick={() => handleAskDelete(row.id)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ]), []);

    return (
        <Box p={2}>
            <Paper elevation={3}>
                <Box p={2} display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                    <Typography variant="h5">Administración - Tablón de noticias</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <TextField
                            size="small"
                            label="Buscar por nombre"
                            value={qDraft}
                            onChange={(e) => setQDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") setQ(qDraft); }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton size="small" onClick={() => setQ(qDraft)} aria-label="buscar">
                                        <Search fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                            sx={{ minWidth: 260 }}
                        />
                        <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh}>Actualizar</Button>
                        <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
                            Nueva noticia
                        </Button>
                    </Stack>
                </Box>
                <Divider />
                <Box sx={{ height: 560, width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        rowCount={rowCount}
                        paginationMode="server"
                        sortingMode="server"
                        sortModel={sortModel}
                        onSortModelChange={(m) => setSortModel(m.length ? m : [{ field: "id", sort: "desc" }])}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(model) => setPaginationModel(model)}
                        disableRowSelectionOnClick
                        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        sx={{ border: 0 }}
                    />
                </Box>
            </Paper>

            {/* Diálogo crear/editar */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>{editing ? "Editar noticia" : "Nueva noticia"}</DialogTitle>
                <Formik
                    initialValues={editing ? { ...editing, fechaFin: editing.fechaFin || "" } : EMPTY_FORM}
                    validationSchema={NoticiaSchema}
                    enableReinitialize
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Nombre"
                                        name="nombre"
                                        value={values.nombre}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.nombre && Boolean(errors.nombre)}
                                        helperText={touched.nombre && errors.nombre}
                                        fullWidth
                                    />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Descripción</Typography>
                                        <RichTextEditor
                                            value={values.descripcion || ""}
                                            onChange={(html) => setFieldValue("descripcion", html)}
                                            placeholder="Escribe la descripción de la noticia…"
                                        />
                                        {touched.descripcion && errors.descripcion && (
                                            <Typography variant="caption" color="error">{errors.descripcion}</Typography>
                                        )}
                                    </Box>
                                    <TextField
                                        label="Fecha fin (opcional)"
                                        name="fechaFin"
                                        type="date"
                                        value={values.fechaFin ? String(values.fechaFin).slice(0, 10) : ""}
                                        onChange={(e) => setFieldValue("fechaFin", e.target.value || "")}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                </Stack>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDialog}>Cancelar</Button>
                                <Button type="submit" variant="contained" disabled={isSubmitting}>
                                    {editing ? "Guardar cambios" : "Crear"}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            {/* Confirmación de borrado */}
            <Dialog open={Boolean(deleteId)} onClose={handleCancelDelete}>
                <DialogTitle>Eliminar noticia</DialogTitle>
                <DialogContent dividers>
                    <Typography>¿Seguro que deseas eliminar la noticia con ID {deleteId}?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete}>Cancelar</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbars */}
            <Snackbar open={Boolean(errorMsg)} autoHideDuration={4500} onClose={() => setErrorMsg(null)}>
                <Alert onClose={() => setErrorMsg(null)} severity="error" variant="filled">{errorMsg}</Alert>
            </Snackbar>
            <Snackbar open={Boolean(okMsg)} autoHideDuration={3000} onClose={() => setOkMsg(null)}>
                <Alert onClose={() => setOkMsg(null)} severity="success" variant="filled">{okMsg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default NoticiasAdmin;
